// Schema definitions for each registry — drives the generic AssetRegistry form.
import { Gauge, Container, GitBranchPlus, ArrowDownToLine, Wind, Bell, Droplets, Zap, AirVent, PlugZap } from "lucide-react";
import { SIGNAL_MODE_OPTIONS } from "../lib/tankLevel";

const COMMON = [
  { key: "name", label: "Name", required: true, full: true },
  { key: "location_label", label: "Location Label", placeholder: "e.g. Pump Room / Tower A — Stair-A", full: true },
  { key: "location", label: "Location Code", placeholder: "basement / terrace / tower_a", kind: "select",
    options: [{ v: "basement", label: "Basement" }, { v: "ground", label: "Ground" }, { v: "terrace", label: "Terrace" }, { v: "tower_a", label: "Tower A" }, { v: "tower_b", label: "Tower B" }, { v: "tower_c", label: "Tower C" }, { v: "pump_room", label: "Pump Room" }, { v: "rooftop", label: "Rooftop" }, { v: "other", label: "Other" }] },
  { key: "zone", label: "Fire Zone (optional)", placeholder: "e.g. Stair-A, Basement — for Cause & Effect zone-scoped rules",
    hint: "A fire-safety grouping, separate from Location — a smoke control zone can span more than one Location" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "status", label: "Status", kind: "select",
    options: ["active", "fault", "offline", "maintenance"].map((v) => ({ v, label: v.toUpperCase() })) },
  { key: "last_tested", label: "Last Tested", kind: "date" },
  { key: "next_service", label: "Next Service / Calibration", kind: "date" },
  { key: "notes", label: "Notes", kind: "textarea", full: true },
];

export const REGISTRY_DEFS = {
  pressure_sensor: {
    title: "Pressure Sensors", icon: Gauge, accentColor: "orange",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster", "terrace_header"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.zone", label: "Zone", kind: "select",
        options: [{ v: "high_zone", label: "High Zone" }, { v: "low_zone", label: "Low Zone" }, { v: "single_zone", label: "Single Zone" }] },
      { key: "meta.tower", label: "Tower (if booster)" },
      { key: "meta.range_bar", label: "Range (bar)", kind: "number" },
      { key: "meta.alarm_high_bar", label: "Alarm High (bar)", kind: "number", required: true, hint: "Mandatory — set per site" },
      { key: "meta.alarm_low_bar", label: "Alarm Low (bar)", kind: "number", required: true, hint: "Mandatory — set per site" },
      ...COMMON.slice(3),
    ],
  },
  tank: {
    title: "Tanks", icon: Container, accentColor: "blue",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.type", label: "Tank Type", kind: "select",
        options: ["UG", "OHT", "break_tank", "terrace", "sprinkler_reserve", "day_tank", "diesel"].map((v) => ({ v, label: v.toUpperCase() })) },

      // ── DIMENSIONS ────────────────────────────────────────────
      { key: "_section_dim", label: "Tank Dimensions (auto-calculates capacity)", kind: "section" },
      { key: "meta.shape", label: "Shape", kind: "select",
        options: [{ v: "rectangular", label: "Rectangular / Cuboidal" }, { v: "cylindrical_vertical", label: "Cylindrical (vertical)" }, { v: "cylindrical_horizontal", label: "Cylindrical (horizontal)" }],
        default: "rectangular" },
      { key: "meta.length_m", label: "Length (m)", kind: "number", step: "0.01", placeholder: "e.g. 6.0" },
      { key: "meta.width_m", label: "Width (m)", kind: "number", step: "0.01", placeholder: "e.g. 4.0" },
      { key: "meta.height_m", label: "Height / Depth (m)", kind: "number", step: "0.01", placeholder: "e.g. 1.8" },
      { key: "meta.diameter_m", label: "Diameter (m, cylindrical)", kind: "number", step: "0.01", placeholder: "e.g. 3.0" },
      { key: "meta.computed_capacity_litres", label: "Computed Capacity", kind: "computed",
        compute: (form) => {
          const m = form.meta || {};
          const L = Number(m.length_m), W = Number(m.width_m), H = Number(m.height_m), D = Number(m.diameter_m);
          let v = 0;
          if (m.shape === "rectangular") v = L * W * H;
          else if (m.shape === "cylindrical_vertical") v = Math.PI * (D / 2) ** 2 * H;
          else if (m.shape === "cylindrical_horizontal") v = Math.PI * (D / 2) ** 2 * L;
          if (!isFinite(v) || v <= 0) return "—";
          const litres = Math.round(v * 1000);
          return `${litres.toLocaleString("en-IN")} L  (${(litres / 1000).toFixed(2)} KL)`;
        } },
      { key: "meta.capacity_kl", label: "Capacity Override (KL)", kind: "number", step: "0.1", placeholder: "leave blank to use computed",
        hint: "Kilolitres (1 KL = 1,000 L). Blank = Computed Capacity is used for litres." },
      { key: "meta.material", label: "Tank Material", placeholder: "RCC / SS304 / HDPE" },

      // ── LEVEL TRANSMITTER (FST700-101 defaults) ───────────────
      { key: "_section_tx", label: "Level Transmitter (FST700-101 default)", hint: "FirstRate FST700-101 Submersible 0–2M 4-20mA 9-30V", kind: "section" },
      { key: "meta.tx_model", label: "Transmitter Model", default: "FST700-101", placeholder: "FST700-101" },
      { key: "meta.tx_range_m", label: "Range (m H₂O)", kind: "number", step: "0.1", default: 2,
        hint: "Measuring range printed on the transmitter label (e.g. 0–5 m → 5). Not the cable length." },
      { key: "meta.tx_signal_output", label: "Signal Output", kind: "select", options: SIGNAL_MODE_OPTIONS, default: "4-20mA",
        hint: "Sets the signal endpoints and fault limits below. 1–5 V / 2–10 V can detect a broken wire; 0–10 V / 0–5 V cannot." },
      { key: "_section_cal", label: "Signal Calibration (optional)", kind: "section",
        hint: "Leave blank to use the Signal Output's defaults — 4–20 mA: 4 / 20 mA, fault below 3.6 or above 21 mA. Confirm against the transmitter datasheet." },
      { key: "meta.signal_min", label: "Signal at empty (min)", kind: "number", step: "0.01", placeholder: "mode default" },
      { key: "meta.signal_max", label: "Signal at full range (max)", kind: "number", step: "0.01", placeholder: "mode default" },
      { key: "meta.fault_low", label: "Fault below", kind: "number", step: "0.01", placeholder: "mode default",
        hint: "Readings below this are a sensor / wiring fault, never an empty tank" },
      { key: "meta.fault_high", label: "Fault above", kind: "number", step: "0.01", placeholder: "mode default" },
      { key: "meta.offset_m", label: "Installation Offset (m)", kind: "number", step: "0.01", placeholder: "0",
        hint: "Added to the measured depth — e.g. +0.10 if the sensor sits 10 cm above the usable floor" },
      { key: "meta.tx_supply_voltage", label: "Supply Voltage", default: "9-30V DC", placeholder: "9-30V DC" },
      { key: "meta.tx_accuracy", label: "Accuracy", default: "±0.5% FS", placeholder: "±0.5% FS" },
      { key: "meta.tx_cable_length_m", label: "Cable Length (m)", kind: "number", step: "0.1", default: 3 },
      { key: "meta.tx_probe_diameter_mm", label: "Probe Diameter (mm)", kind: "number", default: 28 },
      { key: "meta.tx_material", label: "Probe Material", default: "316L", placeholder: "316L" },
      { key: "meta.tx_calibration_date", label: "Last Calibration", kind: "date" },
      { key: "meta.tx_serial", label: "Transmitter Serial #" },

      // ── VALVES / SETPOINTS ────────────────────────────────────
      { key: "_section_op", label: "Valves & Setpoints", kind: "section" },
      { key: "meta.inlet_valve_type", label: "Inlet Valve Type" },
      { key: "meta.outlet_valve_type", label: "Outlet Valve Type" },
      { key: "meta.level_high_pct", label: "Level High (%)", kind: "number", placeholder: "off",
        hint: "Warning alarm at or above this level. Blank = no high alarm." },
      { key: "meta.level_low_pct", label: "Level Low (%)", kind: "number", placeholder: "25",
        hint: "Warning alarm at or below this level (default 25%)" },
      { key: "meta.level_low_low_pct", label: "Level Low-Low (%)", kind: "number", placeholder: "10",
        hint: "Critical alarm at or below this level (default 10%)" },
      { key: "meta.alarm_delay_s", label: "Alarm Delay (s)", kind: "number", placeholder: "30",
        hint: "A condition must last this long before an alarm is raised" },
      { key: "meta.alarm_hysteresis_pct", label: "Alarm Reset Band (%)", kind: "number", step: "0.5", placeholder: "2",
        hint: "A level alarm clears only this far past its threshold — stops alarm chatter" },

      // ── COMMON FOOTER ─────────────────────────────────────────
      { key: "_section_meta", label: "Asset Metadata", kind: "section" },
      ...COMMON.slice(3),
    ],
  },
  valve: {
    title: "Valves", icon: GitBranchPlus, accentColor: "emerald",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster", "ventilation", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.type", label: "Valve Type", kind: "select",
        options: ["gate", "butterfly", "NRV", "globe", "ball"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.size_mm", label: "Size (mm)", kind: "number" },
      { key: "meta.normally", label: "Normally", kind: "select", options: ["open", "closed"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.actuator_type", label: "Actuator", kind: "select", options: ["manual", "motorized", "pneumatic"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "_section_control", label: "Control Link (optional)", hint: "Motorised and pneumatic valves only \u2014 a manual valve has no relay to drive.", kind: "section" },
      { key: "meta.control_io_point_name", label: "Control Relay Register", placeholder: "e.g. slave2-relay5",
        hint: "The writable relay that starts and stops this equipment \u2014 lets a Cause & Effect rule drive it by name instead of by raw coil. Must be an output register (Hardware Gateways \u2192 IO Mappings)." },
      ...COMMON.slice(3),
    ],
  },
  lift: {
    title: "Lifts", icon: ArrowDownToLine, accentColor: "pink",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.lift_type", label: "Lift Type", kind: "select",
        options: ["passenger", "service", "firefighter"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.capacity_kg", label: "Capacity (kg)", kind: "number" },
      { key: "meta.stops", label: "Stops", kind: "number" },
      { key: "meta.recall_floor", label: "Recall Floor", kind: "number" },
      { key: "meta.firefighter_mode", label: "Firefighter Mode", kind: "select",
        options: [{ v: "Phase1", label: "Phase 1 only" }, { v: "Phase2", label: "Phase 2 only" }, { v: "Both", label: "Phase 1 & 2" }] },
      { key: "meta.phase1_tested", label: "Phase 1 Last Tested", kind: "date" },
      { key: "meta.phase2_tested", label: "Phase 2 Last Tested", kind: "date" },
      { key: "_section_telemetry", label: "Live Telemetry Links (optional)", hint: "Same gateway as the Floor link above — leave blank if not available", kind: "section" },
      { key: "meta.direction_io_point_name", label: "Direction Register", placeholder: "e.g. lift1-direction" },
      { key: "meta.door_io_point_name", label: "Door Status Register", placeholder: "e.g. lift1-door" },
      { key: "meta.motor_temp_io_point_name", label: "Motor Temp Register", placeholder: "e.g. lift1-motor-temp" },
      ...COMMON.slice(3),
    ],
  },
  fan: {
    title: "Fans & AHU", icon: Wind, accentColor: "cyan",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["ventilation", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.type", label: "Fan Type", kind: "select",
        options: ["pressurization", "exhaust", "fresh_air", "AHU"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.cfm", label: "CFM Capacity", kind: "number" },
      { key: "meta.kw", label: "kW Rating", kind: "number" },
      { key: "meta.control", label: "Control Type", kind: "select", options: ["on_off", "VFD"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.associated_zone", label: "Associated Zone" },
      { key: "_section_detect", label: "Detection Links (optional)", hint: "Only if this fan's controller reports run feedback separately.", kind: "section" },
      { key: "meta.run_status_io_point_name", label: "Run Status Register", placeholder: "e.g. fan1-run-status",
        hint: "Cause & Effect feedback channel — confirms a rule's Start command actually took effect" },
      { key: "_section_control", label: "Control Link (optional)", hint: "Only if this fan can be started remotely. Leave blank for a fan the platform only monitors.", kind: "section" },
      { key: "meta.control_io_point_name", label: "Control Relay Register", placeholder: "e.g. slave2-relay1",
        hint: "The writable relay that starts and stops this equipment \u2014 lets a Cause & Effect rule drive it by name instead of by raw coil. Must be an output register (Hardware Gateways \u2192 IO Mappings)." },
      ...COMMON.slice(3),
    ],
  },
  fire_alarm_device: {
    title: "Fire Alarm Devices", icon: Bell, accentColor: "red",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["detection", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.device_type", label: "Device Type", kind: "select",
        options: ["smoke", "heat", "MCP", "sounder", "beacon", "io_module"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.loop", label: "Loop #", kind: "number" },
      { key: "meta.address", label: "Address", kind: "number" },
      { key: "meta.zone", label: "Zone" },
      { key: "meta.floor", label: "Floor" },
      { key: "_section_alerts", label: "Alerts", kind: "section",
        hint: "Automatic: when the Live Data Source reads 1 (alarm), a Critical alert goes to WhatsApp, email and the app (Alert Dispatch contacts) and resolves itself when the device is back to normal." },
      ...COMMON.slice(3),
    ],
  },
  pump: {
    title: "Pumps", icon: Droplets, accentColor: "blue",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.pump_type", label: "Pump Type", kind: "select",
        options: ["main_pump", "jockey_pump", "booster_pump", "sprinkler_pump", "hydrant_pump", "pressure_pump", "transfer_pump", "dewatering_pump", "drain_pump", "sewage_pump", "borewell_pump", "domestic_water_pump", "raw_water_pump", "soft_water_pump", "fire_pump"].map((v) => ({ v, label: v.replace(/_/g, " ").toUpperCase() })) },
      { key: "meta.capacity_lpm", label: "Capacity (LPM)", kind: "number" },
      { key: "meta.kw", label: "Power (kW)", kind: "number" },
      { key: "meta.power_source", label: "Power Source", kind: "select",
        options: ["Electric", "Diesel", "Both"].map((v) => ({ v, label: v })) },
      { key: "meta.serial_number", label: "Serial Number" },
      { key: "meta.commissioned_date", label: "Commissioned Date", kind: "date" },
      { key: "_section_detect", label: "Detection Links (optional)", hint: "Pump Run Detection uses the Live Data Source link below — these are separate signals, only if this pump's controller reports them.", kind: "section" },
      { key: "meta.pump_trip_io_point_name", label: "Pump Trip Register", placeholder: "e.g. pump1-trip" },
      { key: "meta.auto_manual_io_point_name", label: "Auto/Manual Register", placeholder: "e.g. pump1-auto-manual" },
      { key: "meta.run_status_io_point_name", label: "Run Status Register", placeholder: "e.g. pump1-run-status",
        hint: "Cause & Effect feedback channel — confirms a rule's Start command actually took effect" },
      // Raised by the server (backend/asset_alarms.py), stored in Alert Management and sent per Alert Dispatch.
      { key: "_section_alerts", label: "Alerts", hint: "Sent to WhatsApp, email and the app (Alert Dispatch contacts) and kept in Alert Management.", kind: "section" },
      { key: "meta.alert_on_run", label: "Alert when pump runs / stops", kind: "toggle",
        hint: "One alert when the pump starts, another when it stops. Uses the Live Data Source below (1 = running)." },
      { key: "meta.run_alert_severity", label: "Run / Stop Alert Level", kind: "select", placeholder: "Warning",
        options: [{ v: "warning", label: "Warning" }, { v: "critical", label: "Critical" }] },
      { key: "_spacer_alerts", kind: "spacer" },
      { key: "meta.alert_on_trip", label: "Alert on pump trip (Critical)", kind: "toggle",
        hint: "Needs the Pump Trip Register above. Resolves itself when the trip clears." },
      { key: "_section_control", label: "Control Link (optional)", hint: "Only if this pump can be started remotely. Leave blank for a pump the platform only monitors.", kind: "section" },
      { key: "meta.control_io_point_name", label: "Control Relay Register", placeholder: "e.g. slave1-relay1",
        hint: "The writable relay that starts and stops this equipment \u2014 lets a Cause & Effect rule drive it by name instead of by raw coil. Must be an output register (Hardware Gateways \u2192 IO Mappings)." },
      ...COMMON.slice(3),
    ],
  },
  mcc_panel: {
    title: "MCC Panels", icon: Zap, accentColor: "amber",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.incoming_voltage", label: "Incoming Voltage (V)", kind: "number" },
      { key: "meta.rated_capacity_amp", label: "Rated Capacity (A)", kind: "number" },
      { key: "meta.number_of_feeders", label: "Number of Feeders", kind: "number" },
      { key: "_section_detect", label: "Detection Links (optional)", hint: "Panel Healthy uses the Live Data Source link below — power status is conditional, only if this panel reports it separately.", kind: "section" },
      { key: "meta.power_status_io_point_name", label: "Power Status Register", placeholder: "e.g. mcc1-power-ok" },
      ...COMMON.slice(3),
    ],
  },
  // One sensor = one Modbus slave reporting several readings, so it has its own register picker
  // (kind "slave_readings", SlaveReadingsFields.jsx) instead of the single Live Data Source link.
  // Status and limits: backend/iaq.py.
  iaq_sensor: {
    title: "IAQ Sensors", icon: AirVent, accentColor: "sky",
    schema: [
      { ...COMMON[0], hint: "Shown as the zone on IAQ Monitor — e.g. Auditorium" },
      { ...COMMON[1], placeholder: "e.g. Level 2" },
      COMMON[2],
      { key: "_readings", kind: "slave_readings", hint: "Use Scale when the sensor sends tenths: temp 274 × 0.1 = 27.4 °C.",
        readings: [
          { key: "co2", label: "CO₂", unit: "ppm" },
          { key: "pm25", label: "PM2.5", unit: "µg/m³" },
          { key: "temp", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "voc", label: "VOC", unit: "" },
        ] },
      { key: "_section_limits", label: "Limits", kind: "section",
        hint: "Status turns Moderate above the first value and Poor above the second. Blank = default. Temperature and humidity don't set the status." },
      { key: "meta.co2_moderate", label: "CO₂ Moderate above (ppm)", kind: "number", placeholder: "800" },
      { key: "meta.co2_poor", label: "CO₂ Poor above (ppm)", kind: "number", placeholder: "1000" },
      { key: "meta.pm25_moderate", label: "PM2.5 Moderate above (µg/m³)", kind: "number", placeholder: "35" },
      { key: "meta.pm25_poor", label: "PM2.5 Poor above (µg/m³)", kind: "number", placeholder: "75" },
      { key: "meta.voc_unit", label: "VOC is reported as", kind: "select", default: "index",
        options: [{ v: "index", label: "VOC index (0–500)" }, { v: "ppb", label: "TVOC (ppb)" }] },
      { key: "_voc_gap", kind: "spacer" },
      { key: "meta.voc_moderate", label: "VOC Moderate above", kind: "number", placeholder: "150 (index) · 220 (ppb)" },
      { key: "meta.voc_poor", label: "VOC Poor above", kind: "number", placeholder: "250 (index) · 660 (ppb)" },
      { key: "_section_meta", label: "Asset Metadata", kind: "section" },
      ...COMMON.slice(4),
    ],
  },
  // Same multi-reading pattern as IAQ sensors. Readings only for now: the Energy Meter Dashboard
  // shows them, plus kWh today / this month / per hour worked out from the kWh counter
  // (backend/energy.py). The counter is required — every energy figure comes from it.
  energy_meter: {
    title: "Energy Meters", icon: PlugZap, accentColor: "amber",
    schema: [
      { ...COMMON[0], placeholder: "e.g. Main Meter — Fire Pump Room" },
      { ...COMMON[1], placeholder: "e.g. Pump Room, Basement" },
      COMMON[2],
      { key: "_readings", kind: "slave_readings", hint: "Use ×0.001 for a meter that counts Wh instead of kWh. Scale is applied before the CT multiplier below.",
        readings: [
          { key: "kwh", label: "Energy (kWh counter)", unit: "kWh", required: true },
          { key: "kw", label: "Power", unit: "kW" },
          { key: "voltage", label: "Voltage", unit: "V" },
          { key: "current", label: "Current", unit: "A" },
          { key: "pf", label: "Power factor", unit: "" },
          { key: "frequency", label: "Frequency", unit: "Hz" },
        ] },
      { key: "_section_ct", label: "Current Transformer (optional)", kind: "section",
        hint: "Only for a meter that reports CT-secondary values. Leave blank if the meter already shows primary values." },
      { key: "meta.ct_multiplier", label: "CT Multiplier", kind: "number", step: "1", placeholder: "1",
        hint: "e.g. 40 for a 200/5 A CT. Multiplies kWh, kW and current." },
      { key: "_section_meta", label: "Asset Metadata", kind: "section" },
      ...COMMON.slice(4),
    ],
  },
};
